import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Mail, User, MessageSquare, Loader2, CheckCircle } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ContactPage = () => {
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill in all required fields");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      setSubmitted(true);
      toast.success("Message sent successfully!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send message. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="page-fade-in">
        <section className="py-32 px-6">
          <div className="max-w-lg mx-auto text-center">
            <div className="w-20 h-20 bg-[#42D78A]/10 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-10 h-10 text-[#42D78A]" />
            </div>
            <h1 className="text-3xl font-bold text-[#1a1a1a] mb-4" style={{ fontFamily: "'Outfit', sans-serif" }}>
              Message Sent!
            </h1>
            <p className="text-gray-600 mb-8" style={{ fontFamily: "'Montserrat', sans-serif" }}>
              Thank you for reaching out. We'll get back to you as soon as possible.
            </p>
            <Button
              onClick={() => { setSubmitted(false); setForm({ name: "", email: "", subject: "", message: "" }); }}
              variant="outline"
              className="rounded-full px-8 py-5 font-bold border-2 border-[#1a1a1a] hover:bg-[#1a1a1a] hover:text-white"
              style={{ fontFamily: "'Outfit', sans-serif" }}
              data-testid="send-another-btn"
            >
              Send Another Message
            </Button>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div className="page-fade-in">
      {/* Hero */}
      <section className="bg-[#EEF8F2] text-[#1a1a1a] border-b border-[#42D78A]/20 py-20 md:py-24 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <p className="text-[#42D78A] text-sm tracking-[0.2em] uppercase mb-4 font-medium" style={{ fontFamily: "'Montserrat', sans-serif" }}>Contact Us</p>
          <h1 className="text-4xl sm:text-5xl font-bold mb-4" style={{ fontFamily: "'Outfit', sans-serif" }}>
            Get in Touch
          </h1>
          <p className="text-gray-600 max-w-xl mx-auto" style={{ fontFamily: "'Montserrat', sans-serif" }}>
            Have a question or want to work with us? Drop us a message and we'll get back to you.
          </p>
        </div>
      </section>

      {/* Form */}
      <section className="py-20 px-6">
        <div className="max-w-2xl mx-auto">
          <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <Label className="text-[#1a1a1a] font-medium text-sm" style={{ fontFamily: "'Outfit', sans-serif" }}>Name *</Label>
                <div className="relative mt-1">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    value={form.name}
                    onChange={(e) => handleChange("name", e.target.value)}
                    placeholder="Your name"
                    className="h-12 pl-10 rounded-lg border-gray-200"
                    data-testid="contact-name"
                    disabled={loading}
                  />
                </div>
              </div>
              <div>
                <Label className="text-[#1a1a1a] font-medium text-sm" style={{ fontFamily: "'Outfit', sans-serif" }}>Email *</Label>
                <div className="relative mt-1">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    type="email"
                    value={form.email}
                    onChange={(e) => handleChange("email", e.target.value)}
                    placeholder="you@email.com"
                    className="h-12 pl-10 rounded-lg border-gray-200"
                    data-testid="contact-email"
                    disabled={loading}
                  />
                </div>
              </div>
            </div>
            <div>
              <Label className="text-[#1a1a1a] font-medium text-sm" style={{ fontFamily: "'Outfit', sans-serif" }}>Subject</Label>
              <Input
                value={form.subject}
                onChange={(e) => handleChange("subject", e.target.value)}
                placeholder="What's this about?"
                className="h-12 mt-1 rounded-lg border-gray-200"
                data-testid="contact-subject"
                disabled={loading}
              />
            </div>
            <div>
              <Label className="text-[#1a1a1a] font-medium text-sm" style={{ fontFamily: "'Outfit', sans-serif" }}>Message *</Label>
              <Textarea
                value={form.message}
                onChange={(e) => handleChange("message", e.target.value)}
                placeholder="Tell us what's on your mind..."
                className="mt-1 min-h-[160px] rounded-lg border-gray-200"
                data-testid="contact-message"
                disabled={loading}
              />
            </div>
            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-[#42D78A] hover:bg-[#3bc27c] text-[#1a1a1a] rounded-full py-6 text-base font-bold transition-all hover:-translate-y-0.5"
              style={{ fontFamily: "'Outfit', sans-serif" }}
              data-testid="contact-submit-btn"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Sending...
                </>
              ) : (
                "Send Message"
              )}
            </Button>
          </form>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;
